import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { getAppState } from "./useAppStatePreservation";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useQueryClient } from "@tanstack/react-query";
import { clearSubscriptionCache } from "@/contexts/SubscriptionContext";
import { setSentryUser, captureError } from "@/lib/sentry";
import { trackEvent } from "@/lib/analytics";
import { recordConsent } from "@/services/consent-service";
import { sendLifecycleEmail } from "@/lib/lifecycle-email";
import {
  mintSessionId,
  markSessionRegistered,
  clearUnconfirmedSession,
  clearSessionRegistry,
  nextRegisterGeneration,
  isCurrentGeneration,
  logSessionDecision,
} from "@/lib/session-registry";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// STRICT SINGLE-DEVICE MODE: register a fresh session_id once per app cold-load
// for any authenticated user, regardless of how the session got there (fresh
// password sign-in, magic link, OAuth, or restored cached session). The
// register call invalidates every other active_sessions row, so opening the
// app on Device B immediately kicks Device A.
let hasRegisteredThisLoad = false;

async function registerDeviceSessionStrict(userId: string, reason: string) {
  if (hasRegisteredThisLoad) return;
  hasRegisteredThisLoad = true;
  const gen = nextRegisterGeneration();
  try {
    const sessionId = mintSessionId();
    const isAndroidDevice = /android/i.test(navigator.userAgent);
    const deviceInfo = `${navigator.userAgent} - ${isAndroidDevice ? 'Android' : 'Other'}`;
    logSessionDecision('register_start', { reason, gen });
    const { data, error } = await supabase.functions.invoke('manage-session', {
      body: { action: 'register', session_id: sessionId, device_info: deviceInfo },
    });
    if (!isCurrentGeneration(gen)) {
      // A newer registration superseded this one — never touch storage.
      logSessionDecision('register_stale_ignored', { reason, gen });
      return;
    }
    if (error) {
      // Transient failure MUST NOT cause a later false logout: drop the
      // unconfirmed id so the validator skips instead of failing forever.
      clearUnconfirmedSession(sessionId);
      hasRegisteredThisLoad = false;
      logSessionDecision('register_failed', { reason, code: (error as any)?.name ?? 'unknown' });
    } else {
      markSessionRegistered(sessionId);
      logSessionDecision('register_ok', { reason, displaced: (data as any)?.displaced_count ?? 0 });
    }
  } catch (err) {
    if (isCurrentGeneration(gen)) hasRegisteredThisLoad = false;
    logSessionDecision('register_exception', { reason });
  }
}

// Boot-time sweep: drop expired/malformed pending_consent payloads even if the
// user never logs in again. GDPR/CCPA data minimization. #7
(function sweepPendingConsent() {
  try {
    const raw = localStorage.getItem("mblearn_pending_consent");
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const expiresAt = parsed?.expiresAt;
    if (!expiresAt || Date.parse(expiresAt) < Date.now()) {
      localStorage.removeItem("mblearn_pending_consent");
    }
  } catch {
    try { localStorage.removeItem("mblearn_pending_consent"); } catch {}
  }
})();

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();
  const languageContext = useLanguage();
  const t = languageContext?.t || ((key: string) => key);
  const queryClient = useQueryClient();

  // Track provider mount count for duplicate-bundle detection
  useEffect(() => {
    _trackAuthProviderMount(1);
    return () => { _trackAuthProviderMount(-1); };
  }, []);

  // Marcar como listo una vez que todo esté inicializado
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Invalidate user-specific queries when user changes (NOT all queries)
  // Blanket invalidateQueries() caused cascading refetch loops across all browsers
  useEffect(() => {
    if (user) {
      console.log('🔄 [Auth] User changed, invalidating user-specific queries');
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["moduleProgress"] });
      queryClient.invalidateQueries({ queryKey: ["institutionalStudent"] });
    }
  }, [user?.id, queryClient]);

  // Clear local state when user signs out (not on page reload)
  useEffect(() => {
    // Solo limpiar si anteriormente había un usuario y ahora no hay
    // Esto evita limpiar en la carga inicial de la página
    const previousUser = sessionStorage.getItem('had_user_session');
    
    if (!user && previousUser === 'true') {
      console.log('🔄 [Auth] User signed out, clearing query cache only (preserving app state)');
      queryClient.clear();
      
      // NO limpiar el estado de la aplicación para permitir restauración después del login
      // Solo limpiar estados temporales específicos de componentes
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // NO limpiar app-complete-state ni app-state-timestamp
        if (key && key !== 'app-complete-state' && key !== 'app-state-timestamp' && (
          key.startsWith('quiz-state-') || 
          key.startsWith('study-state-') || 
          key.startsWith('exam-simulation-state') ||
          key === 'quiz-selected-category' ||
          key === 'study-selected-category'
        )) {
          keysToRemove.push(key);
        }
      }
      
      console.log('🗑️ [Auth] Clearing component states:', keysToRemove);
      keysToRemove.forEach(key => localStorage.removeItem(key));
      sessionStorage.removeItem('had_user_session');
    } else if (user) {
      // Marcar que hay una sesión activa
      sessionStorage.setItem('had_user_session', 'true');
    }
  }, [user, queryClient]);

  // Interceptor para detectar errores 403 de JWT inválido y limpiar tokens huérfanos
  // CRITICAL: This must NOT run before getSession() resolves, otherwise it can
  // clear a valid refresh token before Supabase has a chance to use it.
  // We use `loading` as a gate: only check after initial session hydration is complete.
  useEffect(() => {
    // Don't run while initial session is still loading
    if (loading) return;

    // CRITICAL: Skip orphaned token cleanup during password recovery flow.
    // The recovery token creates a transient state where session=null but
    // valid tokens exist in localStorage. Cleaning them would kill the recovery.
    const isRecoveryFlow = window.location.pathname === '/auth/reset' ||
      window.location.hash.includes('type=recovery');
    if (isRecoveryFlow) {
      console.log('🔑 [Auth] Skipping orphaned token check — recovery flow active');
      return;
    }

    const clearOrphanedAuthState = () => {
      // Limpiar TODOS los tokens de Supabase
      const allKeys = Object.keys(localStorage);
      const supabaseKeys = allKeys.filter(key => 
        key.startsWith('sb-') || 
        key.includes('supabase') ||
        key.includes('auth-token')
      );

      console.log('🗑️ [Auth] Removing orphaned tokens:', supabaseKeys);
      supabaseKeys.forEach(key => localStorage.removeItem(key));

      // Limpiar también sessionStorage
      const allSessionKeys = Object.keys(sessionStorage);
      const supabaseSessionKeys = allSessionKeys.filter(key => 
        key.startsWith('sb-') || 
        key.includes('supabase')
      );
      supabaseSessionKeys.forEach(key => sessionStorage.removeItem(key));

      // Limpiar estado de restauración de app
      localStorage.removeItem('app-complete-state');
      localStorage.removeItem('app-state-timestamp');
      sessionStorage.removeItem('app-complete-state_backup');
      sessionStorage.removeItem('app-state-timestamp_backup');

      // Actualizar estado
      setSession(null);
      setUser(null);
      console.log('✅ [Auth] Orphaned tokens cleaned successfully');
    };

    const getStoredAccessToken = () => {
      const authStorageKey = Object.keys(localStorage).find((key) => /^sb-.*-auth-token$/.test(key));
      if (!authStorageKey) return null;

      const rawValue = localStorage.getItem(authStorageKey);
      if (!rawValue) return null;

      try {
        const parsed = JSON.parse(rawValue);
        const accessToken =
          parsed?.access_token ||
          parsed?.currentSession?.access_token ||
          parsed?.session?.access_token ||
          null;

        return typeof accessToken === 'string' && accessToken.length > 0 ? accessToken : null;
      } catch {
        return null;
      }
    };

    const decodeJwtPayload = (token: string): { sub?: string } | null => {
      try {
        const parts = token.split('.');
        if (parts.length < 2) return null;

        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        const payload = JSON.parse(atob(padded));
        return payload;
      } catch {
        return null;
      }
    };

    const checkAndCleanOrphanedTokens = async () => {
      try {
        // If we already have a valid session, no need to check for orphaned tokens
        if (session) {
          console.log('✅ [Auth] Session exists, skipping orphaned token check');
          return;
        }

        // If there is no persisted auth token, do nothing.
        // This avoids false positives from /user calls without a real user JWT.
        const storedAccessToken = getStoredAccessToken();
        if (!storedAccessToken) {
          return;
        }

        // Fast local validation: token without sub is invalid/orphaned.
        const payload = decodeJwtPayload(storedAccessToken);
        if (!payload?.sub) {
          console.log('🧹 [Auth] Stored token has no sub claim, cleaning up orphaned auth state');
          clearOrphanedAuthState();
          return;
        }

        // Validate current stored token explicitly (instead of implicit getUser with anon context)
        const { error } = await supabase.auth.getUser(storedAccessToken);
        
        if (error) {
          // Only act on confirmed orphaned/deleted-user tokens.
          // Do NOT clear on generic auth/network errors.
          const isOrphanedToken = error.message?.includes('User from sub claim in JWT does not exist');
          
          if (isOrphanedToken) {
            console.log('🧹 [Auth] Detected orphaned JWT token, cleaning up...');
            console.log('🧹 [Auth] Error:', error.message);
            clearOrphanedAuthState();
          }
        }
      } catch (err) {
        console.error('⚠️ [Auth] Error checking tokens:', err);
      }
    };
    
    // Only check after session hydration is complete and there's no session
    checkAndCleanOrphanedTokens();
    
    // On visibility change: re-check for orphaned tokens when app resumes
    const handleVisibility = () => {
      if (!document.hidden) {
        console.log('🔄 [Auth] App resumed, checking tokens');
        checkAndCleanOrphanedTokens();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loading, session]);

  useEffect(() => {
    // Check for recovery token in URL FIRST - highest priority
    const checkRecoveryToken = () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      return hashParams.has('access_token') && hashParams.get('type') === 'recovery';
    };

    // Check for invite token in URL (institutional password setup)
    const checkInviteToken = () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      return hashParams.has('access_token') && hashParams.get('type') === 'invite';
    };

    const hasRecoveryToken = checkRecoveryToken();
    
    if (hasRecoveryToken) {
      console.log('🔑 Recovery token detected in URL - initializing password recovery flow');
      // Don't check existing session, don't redirect - let Supabase process the token
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔐 Auth event:', event, 'Session:', session?.user?.email, 'Path:', window.location.pathname);
        
        // Always update session state
        setSession(session);
        setUser(session?.user ?? null);
        
        // Set Sentry user context for error correlation
        setSentryUser(session?.user?.id ?? null, session?.user?.email);
        
        // Handle password recovery flow with highest priority
        if (event === 'PASSWORD_RECOVERY') {
          console.log('🔑 PASSWORD_RECOVERY event - navigating to reset page');
          navigate('/auth/reset', { replace: true });
          return; // Don't process other logic
        }
        
        if (event === 'SIGNED_IN') {
          // Check if this is a recovery session
          const isRecoveryToken = checkRecoveryToken();
          const isOnResetPage = window.location.pathname === '/auth/reset';
          const isOnLoginPage = window.location.pathname === '/auth/login';
          
          if (isRecoveryToken) {
            console.log('🔑 Recovery token detected in SIGNED_IN event - staying on/navigating to reset page');
            if (!isOnResetPage) {
              navigate('/auth/reset', { replace: true });
            }
            return; // Don't redirect to dashboard
          }

          // Check if this is an invite session (institutional user needing password setup)
          const isInviteToken = checkInviteToken();
          const isOnSetPasswordPage = window.location.pathname === '/auth/set-password';
          
          if (isInviteToken) {
            console.log('🏫 [Auth] Invite token detected in URL hash — redirecting to set-password');
            if (!isOnSetPasswordPage) {
              navigate('/auth/set-password', { replace: true });
            }
            return;
          }
          
          // Only process normal sign in if NOT on reset page
          if (!isOnResetPage) {
            // STRICT single-device: ensure this device's session is registered
            // for EVERY SIGNED_IN event (cached restore, magic link, OAuth…),
            // not just fresh password sign-in.
            if (session?.user) {
              registerDeviceSessionStrict(session.user.id, `signed_in_event`);
            }

            // Flush pending registration consent to DB
            if (session?.user) {
              try {
                const pending = localStorage.getItem("mblearn_pending_consent");
                if (pending) {
                  const parsed = JSON.parse(pending);
                  const { version, language: lang, type, expiresAt } = parsed ?? {};
                  // GDPR/CCPA: drop expired payloads instead of replaying them. #7
                  const expired = expiresAt && Date.parse(expiresAt) < Date.now();
                  if (expired) {
                    localStorage.removeItem("mblearn_pending_consent");
                  } else {
                    recordConsent(session.user.id, type || "registration", lang || "en")
                      .then(() => localStorage.removeItem("mblearn_pending_consent"))
                      .catch(() => {});
                  }
                }
              } catch {
                // Malformed payload — never replay, just purge.
                try { localStorage.removeItem("mblearn_pending_consent"); } catch {}
              }
            }

            // Email confirmation gate retired 2026-06 (auto_confirm_email=true).
            if (isOnLoginPage) {

              // 1. Check URL returnTo param (set by ProtectedRoute)
              const urlParams = new URLSearchParams(window.location.search);
              const returnTo = urlParams.get('returnTo');
              
              if (returnTo && !returnTo.startsWith('/auth/') && returnTo !== '/') {
                console.log(`✅ [Auth] Login successful - returning to: ${returnTo}`);
                navigate(returnTo, { replace: true });
                return;
              }
              
              // 2. Fallback: check saved app state
              const savedState = getAppState();
              const isAuthRoute = savedState.route?.startsWith('/auth/');
              const isRootRoute = savedState.route === '/';
              const isValidRestoreRoute = savedState.route && 
                                          !isAuthRoute && 
                                          !isRootRoute &&
                                          savedState.route !== '/dashboard';
              
              console.log('✅ [Auth] Login successful - navigating to dashboard');
              
              if (isValidRestoreRoute) {
                const timeSinceLastState = Date.now() - savedState.timestamp;
                
                if (timeSinceLastState < 24 * 60 * 60 * 1000) {
                  console.log(`🔄 [Auth] Restoring to: ${savedState.route}`);
                  navigate(savedState.route, { replace: true });
                } else {
                  navigate('/dashboard', { replace: true });
                }
              } else {
                localStorage.removeItem('app-complete-state');
                localStorage.removeItem('app-state-timestamp');
                navigate('/dashboard', { replace: true });
              }
            }
          }
        }
      }
    );

    // Check for existing session ONLY if no recovery token
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;

    if (!hasRecoveryToken) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Email confirmation gate retired 2026-06 (auto_confirm_email=true).


        // STRICT single-device: cold-load with cached session must also
        // displace any other active device. SIGNED_IN does not always fire
        // on restored sessions, so register here too (idempotent via flag).
        if (session?.user) {
          registerDeviceSessionStrict(session.user.id, 'getSession_restore');
        }

        setLoading(false);
      }).catch((err) => {
        console.error('❌ [Auth] getSession failed — unblocking AuthGate:', err);
        captureError(err, { flow: 'auth_hydration', stage: 'getSession' });
        setSession(null);
        setUser(null);
        setLoading(false);
      });

      // Safety timeout: if getSession neither resolves nor rejects within 8s,
      // unblock the gate to avoid permanent white screen
      safetyTimer = setTimeout(() => {
        setLoading((prev) => {
          if (prev) {
            console.error('⏰ [Auth] getSession safety timeout — force-unblocking AuthGate');
            captureError(new Error('Auth hydration timeout'), { flow: 'auth_hydration', stage: 'safety_timeout' });
            return false;
          }
          return prev;
        });
      }, 8000);
    } else {
      setLoading(false);
    }

    return () => {
      if (safetyTimer) clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, [navigate]);

  const signUp = async (email: string, password: string, fullName: string) => {
    // Importante: el link de verificación NO debe aterrizar en una ruta
    // protegida. /auth/callback es público y se encarga de esperar a que
    // Supabase consuma el hash (#access_token=...) y luego navega al
    // dashboard. Antes apuntábamos a /dashboard y ProtectedRoute corría
    // contra la hidratación → spinner infinito.
    const redirectUrl = `${window.location.origin}/auth/callback`;

    // Pre-seed preferred_language into auth user_metadata so the email hook
    // can render signup/recovery/magic-link in the correct locale even before
    // the profile row exists.
    const preferredLanguage =
      (typeof localStorage !== 'undefined' && localStorage.getItem('preferred_language')) ||
      (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'es');

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          preferred_language: preferredLanguage,
        }
      }
    });

    if (!error) {
      trackEvent('user_registered', 'auth', { method: 'email' });
      // Marcador de "verificación pendiente" para que VerifyEmail no
      // expulse al usuario a /auth/login cuando aún no hay sesión
      // (Supabase no crea sesión hasta confirmar email).
      try {
        sessionStorage.setItem("mblearn:pending_verification_email", email);
      } catch {}
      toast.success(t("auth.accountCreatedVerifyEmail"));
      // Redirigir a la página de verificación
      setTimeout(() => navigate('/auth/verify-email', { replace: true }), 300);
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const isAndroid = /android/i.test(navigator.userAgent);
    console.log(`🔐 [Auth] SignIn attempt - Android: ${isAndroid}`);

    // Claim the registration slot BEFORE the auth call. Otherwise the
    // SIGNED_IN event (which fires while we are still awaiting) spawns its own
    // registerDeviceSessionStrict → two concurrent `register` calls → the
    // slower one's invalidate_other_sessions deletes the row of the id we
    // actually kept in storage → false "session invalidated" logout ~40s later.
    hasRegisteredThisLoad = true;

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    
    if (error) {
      hasRegisteredThisLoad = false; // release the slot: no session to register
      console.error('❌ [Auth] SignIn error:', error);

      // Friendly message for the most common case; never surface raw provider text.
      const msg = /invalid login credentials|invalid_credentials/i.test(error.message || '')
        ? 'No pudimos ingresar. Revisa que el correo y la contraseña estén correctos. Si usas una contraseña temporal, cópiala exactamente como aparece en tu email.'
        : (t("auth.errorSignIn") + (error.message || ''));
      toast.error(msg);
    } else {
      console.log('✅ [Auth] SignIn successful');
      trackEvent('user_logged_in', 'auth', { method: 'email' });
      // Silent sign-in: no "welcome back" banner (removed by product decision).
      
      // Register session in backend — fire-and-forget to avoid blocking login.
      // Mark the strict-mode flag so the SIGNED_IN / getSession paths don't
      // also register and overwrite the session_id we just minted here.
      hasRegisteredThisLoad = true;
      const registerGen = nextRegisterGeneration();
      const sessionId = mintSessionId();
      
      const isAndroidDevice = /android/i.test(navigator.userAgent);
      const deviceInfo = `${navigator.userAgent} - ${isAndroidDevice ? 'Android' : 'Other'}`;
      
      console.log('📝 [Auth] Registering session (non-blocking):', { sessionId });
      
      supabase.functions.invoke('manage-session', {
        body: {
          action: 'register',
          session_id: sessionId,
          device_info: deviceInfo
        }
      }).then(({ data, error: sessionError }) => {
        if (!isCurrentGeneration(registerGen)) {
          logSessionDecision('register_stale_ignored', { reason: 'sign_in' });
          return;
        }
        if (sessionError) {
          console.error('⚠️ [Auth] Session registration error:', sessionError);
          // Never leave an unconfirmed id behind — it would make every later
          // validation fail and log the user out (P0 iOS logout bug).
          clearUnconfirmedSession(sessionId);
          hasRegisteredThisLoad = false;
          logSessionDecision('register_failed', { reason: 'sign_in' });
        } else {
          markSessionRegistered(sessionId);
          console.log('✅ [Auth] Session registered successfully', data);
          // Only surface the banner when the server confirms a REAL remote
          // revocation (a live session on a *different* device). Stale rows
          // from this same device (reinstall, app killed, re-login, token
          // refresh) no longer produce a false positive.
          const payload = data as { displaced_count?: number; displaced_remote?: boolean } | null;
          if (payload?.displaced_remote === true) {
            toast.info(t('auth.displacedOtherDevice'));
          }

        }
      }).catch(err => {
        console.error('❌ [Auth] Exception registering session:', err);
        if (isCurrentGeneration(registerGen)) {
          clearUnconfirmedSession(sessionId);
          hasRegisteredThisLoad = false;
          logSessionDecision('register_exception', { reason: 'sign_in' });
        }
      });
      
      // Marcar que hay sesión activa
      sessionStorage.setItem('had_user_session', 'true');
      
      // La navegación será manejada por onAuthStateChange
      console.log('⏭️ [Auth] Navigation will be handled by auth state change');
    }
    
    return { error };
  };

  const signOut = async () => {
    const isAndroid = /android/i.test(navigator.userAgent);
    const isChrome = /chrome/i.test(navigator.userAgent);
    
    try {
      console.log(`🚪 [Auth] SignOut - Android: ${isAndroid}, Chrome: ${isChrome}`);
      trackEvent('user_logged_out', 'auth');
      setSentryUser(null);
      clearSubscriptionCache();
      // Strict mode: allow next sign-in (or cached restore) to re-register.
      hasRegisteredThisLoad = false;
      // Limpiar sesión en backend primero
      const sessionId = localStorage.getItem('current_session_id');
      if (sessionId) {
        try {
          console.log('🗑️ [Auth] Cleaning up backend session:', sessionId);
          await supabase.functions.invoke('manage-session', {
            body: {
              action: 'cleanup',
              session_id: sessionId
            }
          });
          console.log('✅ [Auth] Backend session cleaned');
        } catch (cleanupErr) {
          console.error('⚠️ [Auth] Backend cleanup error:', cleanupErr);
        }
      }
      
      // Marcar que el usuario cerró sesión intencionalmente
      sessionStorage.removeItem('had_user_session');
      
      // Sign out from Supabase — global scope to revoke server-side tokens
      console.log('🔓 [Auth] Signing out from Supabase (global)');
      let signOutError: Error | null = null;
      try {
        const { error } = await supabase.auth.signOut({ scope: 'global' });
        if (error) {
          console.warn('⚠️ [Auth] Global signOut failed, falling back to local:', error.message);
          // Fallback to local if global fails (e.g. network issues)
          const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
          if (localError) {
            console.error('❌ [Auth] Local signOut also failed:', localError);
            signOutError = localError;
          }
        }
      } catch (err) {
        console.warn('⚠️ [Auth] Global signOut exception, falling back to local:', err);
        const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
        if (localError) {
          console.error('❌ [Auth] Local signOut also failed:', localError);
          signOutError = localError;
        }
      }
      
      // Clear all local state immediately
      setSession(null);
      setUser(null);
      
      // Limpieza ULTRA exhaustiva para Android y Chrome
      if (isAndroid || isChrome) {
        console.log('🧹 [Auth] Performing ULTRA deep cleanup for Android/Chrome');
        
        // 0. CRITICAL: Limpiar app state restoration PRIMERO
        console.log('🗑️ [Cleanup] Clearing app state restoration keys...');
        localStorage.removeItem('app-complete-state');
        localStorage.removeItem('app-state-timestamp');
        sessionStorage.removeItem('app-complete-state_backup');
        sessionStorage.removeItem('app-state-timestamp_backup');
        console.log('✅ [Cleanup] App state restoration keys cleared');
        
        // 1. Limpiar TODO excepto idioma
        const keysToKeep = ['preferred_language'];
        const allLocalStorageKeys = Object.keys(localStorage);
        
        console.log(`🗑️ [Cleanup] Found ${allLocalStorageKeys.length} localStorage keys`);
        
        allLocalStorageKeys.forEach(key => {
          if (!keysToKeep.includes(key)) {
            localStorage.removeItem(key);
            console.log(`🗑️ [Cleanup] Removed: ${key}`);
          }
        });
        
        // 2. Limpiar TODO el sessionStorage sin excepciones
        console.log('🗑️ [Cleanup] Clearing ALL sessionStorage');
        sessionStorage.clear();
        
        // 3. Limpiar cookies de Supabase específicamente PRIMERO
        console.log('🍪 [Cleanup] Clearing Supabase-specific cookies');
        const supabaseCookiePatterns = [
          'sb-', 
          'supabase',
          'auth-token',
          'access-token',
          'refresh-token'
        ];
        
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          
          // Verificar si es una cookie de Supabase
          const isSupabaseCookie = supabaseCookiePatterns.some(pattern => 
            name.toLowerCase().includes(pattern.toLowerCase())
          );
          
          if (isSupabaseCookie) {
            // Eliminar con diferentes rutas y dominios
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
            // También intentar con subdominio
            const parts = window.location.hostname.split('.');
            if (parts.length > 2) {
              const baseDomain = parts.slice(-2).join('.');
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${baseDomain}`;
            }
            console.log(`🍪 [Cleanup] Deleted Supabase cookie: ${name}`);
          }
        }
        
        // 3b. Limpiar TODAS las demás cookies
        console.log('🍪 [Cleanup] Clearing ALL remaining cookies');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          
          // Eliminar con diferentes rutas y dominios
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
        }
        
        // 4. Limpiar IndexedDB (donde Supabase puede guardar datos)
        if ('indexedDB' in window) {
          console.log('🗄️ [Cleanup] Clearing IndexedDB databases');
          try {
            const databases = await indexedDB.databases();
            databases.forEach(db => {
              if (db.name) {
                indexedDB.deleteDatabase(db.name);
                console.log(`🗄️ [Cleanup] Deleted database: ${db.name}`);
              }
            });
          } catch (idbError) {
            console.error('⚠️ [Cleanup] IndexedDB cleanup error:', idbError);
          }
        }
        
        // 5. Limpiar cache storage
        if ('caches' in window) {
          console.log('💾 [Cleanup] Clearing cache storage');
          try {
            const cacheNames = await caches.keys();
            await Promise.all(
              cacheNames.map(cacheName => {
                console.log(`💾 [Cleanup] Deleting cache: ${cacheName}`);
                return caches.delete(cacheName);
              })
            );
          } catch (cacheError) {
            console.error('⚠️ [Cleanup] Cache cleanup error:', cacheError);
          }
        }
        
        console.log('✅ [Cleanup] ULTRA deep cleanup complete');
        
        // 6. VERIFICACIÓN FINAL: Asegurar que NO quedan tokens de Supabase
        console.log('🔍 [Cleanup] Final verification - checking for remaining Supabase tokens...');
        const remainingKeys = Object.keys(localStorage).filter(key => 
          key.startsWith('sb-') || 
          key.includes('supabase') ||
          key.includes('auth-token')
        );
        
        if (remainingKeys.length > 0) {
          console.warn('⚠️ [Cleanup] Found remaining Supabase keys, removing them:', remainingKeys);
          remainingKeys.forEach(key => {
            localStorage.removeItem(key);
            console.log(`🗑️ [Cleanup] Final removal: ${key}`);
          });
        } else {
          console.log('✅ [Cleanup] No remaining Supabase tokens found');
        }
        
        // Verificar sessionStorage también
        const remainingSessionKeys = Object.keys(sessionStorage).filter(key => 
          key.startsWith('sb-') || 
          key.includes('supabase')
        );
        
        if (remainingSessionKeys.length > 0) {
          console.warn('⚠️ [Cleanup] Found remaining Supabase keys in sessionStorage:', remainingSessionKeys);
          remainingSessionKeys.forEach(key => sessionStorage.removeItem(key));
        }
      } else {
        // Limpieza normal para otros dispositivos
        console.log('🧹 [Auth] Performing standard cleanup');
        const keysToRemove = Object.keys(localStorage).filter(key => 
          key.startsWith('sb-') || 
          key.includes('supabase') ||
          key === 'current_session_id' ||
          key.startsWith('quiz-') ||
          key.startsWith('study-') ||
          key.startsWith('exam-') ||
          key.includes('subscription')
        );
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          console.log('🗑️ [Auth] Removed:', key);
        });
      }
      
      console.log('✅ [Auth] Sign out complete');
      toast.success(t("auth.sessionClosed"));
      
      // Force full page reload on ALL platforms to guarantee clean React tree
      // This prevents stale component state from leaking between user sessions
      console.log('🔄 [Auth] Forcing complete page reload for clean state');
      setTimeout(() => {
        window.location.replace('/auth/login');
      }, isAndroid || isChrome ? 500 : 100);
    } catch (error) {
      console.error('❌ [Auth] Exception during sign out:', error);
      
      // Limpieza de emergencia ULTRA EXHAUSTIVA
      console.log('🚨 [Auth] Emergency ULTRA cleanup');
      setSession(null);
      setUser(null);
      
      // 1. Limpiar estado de restauración
      localStorage.removeItem('app-complete-state');
      localStorage.removeItem('app-state-timestamp');
      sessionStorage.removeItem('app-complete-state_backup');
      sessionStorage.removeItem('app-state-timestamp_backup');
      
      // 2. Limpiar específicamente tokens de Supabase PRIMERO
      const allKeys = Object.keys(localStorage);
      const supabaseKeys = allKeys.filter(key => 
        key.startsWith('sb-') || 
        key.includes('supabase') ||
        key.includes('auth-token')
      );
      console.log('🗑️ [Emergency] Removing Supabase keys:', supabaseKeys);
      supabaseKeys.forEach(key => localStorage.removeItem(key));
      
      // 3. Limpieza nuclear del resto (excepto idioma)
      const preferredLang = localStorage.getItem('preferred_language');
      localStorage.clear();
      if (preferredLang) {
        localStorage.setItem('preferred_language', preferredLang);
      }
      sessionStorage.clear();
      
      // 4. Eliminar TODAS las cookies con enfoque en Supabase
      const cookies = document.cookie.split(";");
      cookies.forEach((c) => {
        const name = c.split('=')[0].trim();
        // Eliminar con múltiples rutas y dominios
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
        console.log(`🍪 [Emergency] Deleted cookie: ${name}`);
      });
      
      console.log('✅ [Emergency] Emergency cleanup complete');
      toast.success(t("auth.sessionClosed"));
      
      // Forzar reload
      window.location.replace('/auth/login');
    }
  };

  const resetPassword = async (email: string) => {
    // Use the deployed URL for production, or current origin for development
    const baseUrl = window.location.hostname === 'localhost' 
      ? window.location.origin 
      : 'https://www.mblearn.app';
    const redirectUrl = `${baseUrl}/auth/reset`;
    
    console.log('🔑 Sending password reset email with redirect:', redirectUrl);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    
    // No toast here — callers (ForgotPassword) handle their own feedback
    
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (!error) {
      toast.success(t("auth.passwordUpdated"));
      // #38 — Security notice: confirm the password change by email.
      // Best-effort, non-blocking; idempotency key per user+day to dedupe
      // accidental double-clicks in the same session.
      const u = user;
      if (u?.email) {
        const dayKey = new Date().toISOString().slice(0, 10);
        const langPref = (languageContext?.language === "en" ? "en" : "es") as "es" | "en";
        void sendLifecycleEmail({
          baseTemplate: "password-changed",
          recipientEmail: u.email,
          idempotencyKey: `pwd-changed-${u.id}-${dayKey}`,
          userId: u.id,
          lang: langPref,
          templateData: {
            userName: (u.user_metadata as any)?.full_name || u.email,
            changedAt: new Date().toISOString(),
          },
        });
      }
      // Navigation is the caller's responsibility — each page
      // (ResetPassword, SetPassword, Settings) decides where to go.
    }
    
    return { error };
  };


  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useOptionalAuth — Safe hook for components that may render outside AuthProvider.
 * Returns null for user/session instead of throwing or reporting to Sentry.
 * Use this in public pages (FAQ, Terms, Privacy, etc.) that only need auth
 * for conditional UI (e.g. "back to dashboard" vs "back to home").
 */
export const useOptionalAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    return { user: null, session: null, loading: false };
  }
  return { user: context.user, session: context.session, loading: context.loading };
};

// Singleton guard: detect duplicate bundle / double-mount of AuthContext
let _authProviderMountCount = 0;
export const _getAuthProviderMountCount = () => _authProviderMountCount;

// Called from AuthProvider on mount/unmount to track instances
export const _trackAuthProviderMount = (delta: 1 | -1) => {
  _authProviderMountCount += delta;
  if (_authProviderMountCount > 1) {
    console.error(
      `🔴 [useAuth] Multiple AuthProvider instances detected: ${_authProviderMountCount}`
    );
  }
};

/**
 * Detects if a hard reload / update was recently triggered,
 * which explains transient context-less renders.
 */
const detectReloadContext = (): { duringReload: boolean; signal: string } => {
  const params = new URLSearchParams(window.location.search);
  if (params.has('updated')) return { duringReload: true, signal: 'updated-param' };

  try {
    const raw = sessionStorage.getItem('last-update-attempt');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.ts && Date.now() - parsed.ts < 15_000) {
        return { duringReload: true, signal: 'recent-update-attempt' };
      }
    }
  } catch (_) {}

  if (document.readyState !== 'complete') {
    return { duringReload: true, signal: 'document-not-ready' };
  }

  return { duringReload: false, signal: 'normal-navigation' };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    const { duringReload, signal } = detectReloadContext();
    const tag = duringReload
      ? 'auth_context_missing_during_reload'
      : 'auth_context_missing_normal_navigation';

    const diagnostics = {
      pathname: window.location.pathname,
      search: window.location.search,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      readyState: document.readyState,
      rootChildren: document.getElementById('root')?.childElementCount ?? -1,
      providerMountCount: _authProviderMountCount,
      reloadSignal: signal,
      tag,
    };
    const stack = new Error('[useAuth] Context undefined').stack ?? '';
    const shortStack = stack.split('\n').slice(0, 6).join('\n');

    console.error(
      `⚠️ [useAuth] ${tag}\n` +
      `  path: ${diagnostics.pathname}${diagnostics.search}\n` +
      `  time: ${diagnostics.timestamp}\n` +
      `  readyState: ${diagnostics.readyState}\n` +
      `  providerMounts: ${diagnostics.providerMountCount}\n` +
      `  reloadSignal: ${signal}\n` +
      `  stack: ${shortStack}`
    );

    // Report to Sentry with tag for filtering
    try {
      import("@sentry/react").then(Sentry => {
        Sentry.withScope(scope => {
          scope.setTag('auth_context_issue', tag);
          scope.setTag('reload_signal', signal);
          scope.setLevel(duringReload ? 'warning' : 'error');
          scope.setExtras({ ...diagnostics, shortStack });
          Sentry.captureException(
            new Error(`useAuth outside AuthProvider [${tag}]`)
          );
        });
      });
    } catch (_) { /* Sentry may not be initialized */ }

    return {
      user: null,
      session: null,
      loading: true,
      signUp: async () => ({ error: new Error("AuthProvider not mounted") }),
      signIn: async () => ({ error: new Error("AuthProvider not mounted") }),
      signOut: async () => {},
      resetPassword: async () => ({ error: new Error("AuthProvider not mounted") }),
      updatePassword: async () => ({ error: new Error("AuthProvider not mounted") }),
    } as AuthContextType;
  }
  return context;
};
